import { Energy, Facility, HeatSource, RefrigerantSource, WaterSource } from "../../../../api/facility";
import { formatDecimalValue } from "../../../../utils/utils";
import { EMPTY_VALUE } from "../constants/facilityDrawer.constants";
import { FacilityDetailsViewModel, FacilitySourceTableRow } from "../types/facilityDrawer.types";
import {
  buildFacilityInfoRows,
  getFacilityOwnershipLabel,
  getFacilityTypeLabel,
  resolveOptionLabel
} from "./facilitySourceRows.utils";

type OptionItem = { text?: string; value?: string | number };
type CountryOption = { id?: string; country?: string; value?: string; text?: string };
type FacilityLike = Partial<Facility> | undefined;

const mapEnergyDetailRows = (
  energy: Energy[] | undefined,
  energyTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  (energy ?? []).map((item, index) => {
    const kWhValue = Number(item.kWhConsumed);
    const percentage = Number(item.Percentage);

    return {
      id: String(item.id ?? index),
      cells: [
        resolveOptionLabel(energyTypes, item.EnergyType),
        item.Year !== undefined && item.Year !== null ? String(item.Year) : EMPTY_VALUE,
        Number.isNaN(kWhValue) ? EMPTY_VALUE : kWhValue.toLocaleString(),
        Number.isNaN(percentage) ? EMPTY_VALUE : `${percentage}%`,
        formatDecimalValue(item.Price)
      ]
    };
  });

const mapVolumeDetailRows = <
  T extends { id?: string; Year: number; Volumes: number; Price: number; UnitType?: string }
>(
  records: T[] | undefined,
  types: OptionItem[] | undefined,
  getSourceValue: (item: T) => string | number | undefined,
  staticUnit?: string
): FacilitySourceTableRow[] =>
  (records ?? []).map((item, index) => {
    const volume = Number(item.Volumes);

    return {
      id: String(item.id ?? index),
      cells: [
        resolveOptionLabel(types, getSourceValue(item)),
        item.Year !== undefined && item.Year !== null ? String(item.Year) : EMPTY_VALUE,
        `${Number.isNaN(volume) ? 0 : volume.toLocaleString()} ${staticUnit ?? item.UnitType ?? ""}`.trim(),
        formatDecimalValue(item.Price)
      ]
    };
  });

const mapHeatDetailRows = (
  heatSources: HeatSource[] | undefined,
  heatTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  mapVolumeDetailRows(heatSources, heatTypes, item => item.HeatSource);

const mapWaterDetailRows = (
  waterSources: WaterSource[] | undefined,
  waterTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  mapVolumeDetailRows(waterSources, waterTypes, item => item.WaterSource, "m³");

const mapRefrigerantDetailRows = (
  refrigerantSources: RefrigerantSource[] | undefined,
  refrigerantTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  mapVolumeDetailRows(refrigerantSources, refrigerantTypes, item => item.RefrigerantSource);

export const buildFacilityDetailsViewModel = ({
  facility,
  countries,
  energyTypes,
  heatTypes,
  waterTypes,
  refrigerantTypes
}: {
  facility: FacilityLike;
  countries: CountryOption[] | undefined;
  energyTypes: OptionItem[] | undefined;
  heatTypes: OptionItem[] | undefined;
  waterTypes: OptionItem[] | undefined;
  refrigerantTypes: OptionItem[] | undefined;
}): FacilityDetailsViewModel => ({
  title: facility?.facilityName || "Facility",
  facilityTypeLabel: getFacilityTypeLabel(facility?.facilityType),
  facilityOwnershipLabel: getFacilityOwnershipLabel(facility?.facilityOwnership),
  infoRows: buildFacilityInfoRows(facility, countries),
  energyRows: mapEnergyDetailRows(facility?.energy, energyTypes),
  heatRows: mapHeatDetailRows(facility?.heatSource, heatTypes),
  waterRows: mapWaterDetailRows(facility?.waterSource, waterTypes),
  refrigerantRows: mapRefrigerantDetailRows(facility?.refrigerantSource, refrigerantTypes)
});
